import React, {useState, useCallback} from "react";
import styled from "@emotion/styled";
import ByTheNumberLogo from "assets/images/contextual/by_the_number.svg";
import PlayerOfTheGame from "assets/images/contextual/player_of_the_game.svg";
import Spinball from "assets/images/contextual/spinball.svg";
import BeatTheStreak from "assets/images/contextual/beat_the_streak.svg";
import {Typography, Stack} from "@mui/material";
import {KeyboardArrowRightSharp} from "@mui/icons-material";
import {BASE_DOMAIN} from "modules/constants";
import {useSwipeable} from "react-swipeable";
import {useSelector, useDispatch} from "react-redux";
import {
	getContextualPromptData,
	getContextualPromptIsOpen,
} from "modules/selectors/contextualPrompt";
import {declineContextualPrompt, IPrompt} from "modules/actions";
import RallySSO from "modules/utils/SSO/rallySSO";
import {useTranslation} from "react-i18next";
import {ContextualProjects} from "modules/enums";
import {theme} from "assets/theme";
import {getLoginPayload} from "modules/selectors";
import {noop} from "lodash";
import {createFanHubEventScriptTag, EventGUID} from "modules/utils/Services/analytics";

const Wrapper = styled.div`
	position: fixed;
	bottom: 35px;
	width: calc(100% - 40px);
	left: 20px;
	z-index: 99999;
	display: flex;
	flex-flow: column;
	gap: 16px;

	pointer-events: none;
	opacity: 0;
	transform: translateY(200%);
	transition: all 0.3s ease-in-out 0.5s;
	&.open {
		transform: translateY(0);
		pointer-events: auto;
		opacity: 1;
	}

	${theme.breakpoints.down("md")} {
		width: calc(100% - 20px);
		left: 10px;
	}
`;
const Button = styled("button", {
	shouldForwardProp: (prop) => !["backgroundColor", "textColor", "position"].includes(prop),
})<{
	backgroundColor: string;
	textColor: string;
	position: number;
}>`
	padding: 8px 12px;
	outline: none;
	box-shadow: none;
	display: flex;
	background: ${(props) => props.backgroundColor};
	color: ${(props) => props.textColor};
	border: none;
	gap: 8px;
	align-items: center;
	justify-content: flex-start;

	transition: ${(props) => (props.position === 0 ? "all 0.3s ease-in-out 0.5s" : "none")};
	transform: translateX(${(props) => props.position}px);
	img {
		max-height: 32px;
		max-width: 90px;
	}
`;

const Divider = styled.div`
	width: 1px;
	background: currentColor;
	height: 32px;
`;
/**
 * case BEAT_THE_STREAK = 'beat_the_streak';
 * case BY_THE_NUMBER = 'by_the_number';
 * case SPINBALL = 'spinball';
 * case PLAYER_OF_THE_GAME = 'player_of_the_game';
 */

const dataByGame: Record<
	ContextualProjects,
	{
		title: string;
		color: string;
		background: string;
		image: string;
		deepLink: string;
		webLink: string;
		callback: () => void;
	}
> = {
	[ContextualProjects.BEAT_THE_STREAK]: {
		title: "Play Beat the Streak!",
		color: "#fff",
		background: "#27552B",
		image: BeatTheStreak,
		deepLink: "mlbatbat://gaminghub?gameId=BeatTheStreak2022",
		webLink: `${BASE_DOMAIN}/play?gameId=BeatTheStreak2022`,
		callback: () => {
			createFanHubEventScriptTag(EventGUID.BtnToBTS);
		},
	},
	[ContextualProjects.BY_THE_NUMBER]: {
		title: "Play By the Numbers!",
		color: "#fff",
		background: "#1271F0",
		image: ByTheNumberLogo,
		deepLink: "mlbatbat://gaminghub?gameId=ByTheNumbers2022",
		webLink: `${BASE_DOMAIN}/play?gameId=ByTheNumbers2022`,
		callback: noop,
	},
	[ContextualProjects.SPINBALL]: {
		title: "Come play Spinball Today!",
		color: "#1C1C1E",
		background: "#21CFDC",
		image: Spinball,
		deepLink: "mlbatbat://gaminghub?gameId=Spinball2024",
		webLink: `${BASE_DOMAIN}/play?gameId=Spinball2024`,
		callback: () => {
			createFanHubEventScriptTag(EventGUID.BtnToSpinball);
		},
	},
	[ContextualProjects.PLAYER_OF_THE_GAME]: {
		title: "Vote for Player of the Game",
		image: PlayerOfTheGame,
		color: "#1271F0",
		background: "#fff",
		deepLink: "mlbatbat://webview?url=https://www.mlb.com/apps/player-of-the-game/game",
		webLink: `${BASE_DOMAIN}/apps/player-of-the-game/game`,
		callback: () => {
			createFanHubEventScriptTag(EventGUID.BtnToPOTG);
		},
	},
};

export const ContextualPromptBanner: React.FC<{
	banner: IPrompt;
	onRemove: () => void;
}> = ({banner, onRemove}) => {
	const {i18n} = useTranslation();
	const [position, setPosition] = useState(0);
	const swipeHandlers = useSwipeable({
		onSwipedLeft: () => onRemove(),
		onSwiping: (eventData) => {
			setPosition(eventData.deltaX);
		},
		onSwiped: () => {
			setPosition(0);
		},
		preventScrollOnSwipe: true,
		trackMouse: true,
	});

	if (!banner.game) {
		return null;
	}

	const data = dataByGame[banner.game];
	const messageByLocale =
		banner.message[i18n.language.includes("es") ? "es" : "en"] ||
		banner.message["en"] ||
		data.title;
	const message = i18n.t(messageByLocale, messageByLocale);
	if (!data) {
		return null;
	}

	const toProject = () => {
		try {
			if (RallySSO.IS_NATIVE) {
				RallySSO.launchExternal(data.deepLink);
			} else {
				window.location.href = data.webLink;
			}
		} catch (e) {
			console.log(e);
		}
	};

	return (
		<Stack width="100%" {...swipeHandlers}>
			<Button
				backgroundColor={data.background}
				textColor={data.color}
				onClick={toProject}
				position={position}>
				<img src={data.image} alt="" />

				<Divider />
				<Typography
					variant="body1"
					fontSize={{xs: "14px", md: "16px"}}
					fontWeight="700"
					color="inherit"
					sx={{px: 0.5}}>
					{message}
				</Typography>
				<KeyboardArrowRightSharp sx={{color: "currentColor", ml: "auto"}} />
			</Button>
		</Stack>
	);
};

export const ContextualPromptBanners: React.FC = () => {
	const dispatch = useDispatch();
	const promptContent: IPrompt[] = useSelector(getContextualPromptData);
	const isOpen: boolean = useSelector(getContextualPromptIsOpen);
	const loginPayload = useSelector(getLoginPayload);

	// useEffect(() => {
	// 	dispatch(fetchContextualPrompt())
	// }, [dispatch]);
	const onRemove = useCallback(
		(game: ContextualProjects) => {
			dispatch(
				declineContextualPrompt({
					game,
					uid: loginPayload.uid,
				})
			);
		},
		[loginPayload.uid, dispatch]
	);

	return (
		<Wrapper className={isOpen ? "open" : "close"}>
			{promptContent.map((banner) => (
				<ContextualPromptBanner
					key={banner.game}
					banner={banner}
					onRemove={() => onRemove(banner.game)}
				/>
			))}
		</Wrapper>
	);
};
